import { BanknoteArrowDown, CircleCheck } from 'lucide-react'

import { LoadingSpinner } from '~/components/component-library/LoadingSpinner'
import { CheckCircle } from '~/icons/CheckCircle'
import { OctagonAlert } from '~/icons/OctagonAlert'
import { TriangleAlert } from '~/icons/TriangleAlert'
import { BackendTransaction, TransactionStatus } from '~/types'
import { parseTransactionMetadata } from '~/utils/parseTransactionMetadata'

const SecondaryDisplayWrapper = ({
  children,
}: {
  children: React.ReactNode
}) => {
  return <div className="flex items-center gap-1.5">{children}</div>
}

export const buildSecondaryDisplay = (
  transaction: BackendTransaction,
  approvalsLeft: number,
) => {
  switch (transaction.status) {
    case TransactionStatus.Succeeded: {
      const text = 'Completed'
      if (!transaction.isMerkleTransaction) {
        const transactionMetadata = parseTransactionMetadata(
          transaction.transactionMetadata,
        )

        // bridgeData means there are multiple legs to this transaction
        if (transactionMetadata.bridgeData) {
          const status = transactionMetadata.bridgeData.status

          if (status === 'PENDING') {
            return (
              <SecondaryDisplayWrapper>
                <LoadingSpinner size="lg" />
                <span>Confirming</span>
              </SecondaryDisplayWrapper>
            )
          }

          if (status === 'FAILURE') {
            return (
              <SecondaryDisplayWrapper>
                <BanknoteArrowDown className="size-4 text-secondary" />
                <span>Refunded</span>
              </SecondaryDisplayWrapper>
            )
          }

          if (status === 'SUCCESS') {
            return (
              <SecondaryDisplayWrapper>
                <CheckCircle className="size-4 text-green-500" />
                <span>Completed</span>
              </SecondaryDisplayWrapper>
            )
          }
        }
      }

      return (
        <SecondaryDisplayWrapper>
          <CheckCircle className="size-4 text-green-500" />
          <span>{text}</span>
        </SecondaryDisplayWrapper>
      )
    }
    case TransactionStatus.Failed:
      return (
        <SecondaryDisplayWrapper>
          <OctagonAlert className="size-4 fill-red-500" />
          <span>Failed</span>
        </SecondaryDisplayWrapper>
      )
    case TransactionStatus.Cancelled:
      return (
        <SecondaryDisplayWrapper>
          <TriangleAlert className="size-4 fill-orange-500" />
          <span>Cancelled</span>
        </SecondaryDisplayWrapper>
      )
    case TransactionStatus.Expired:
      return (
        <SecondaryDisplayWrapper>
          <TriangleAlert className="size-4 fill-orange-500" />
          <span>Expired</span>
        </SecondaryDisplayWrapper>
      )
    case TransactionStatus.Submitted:
      return (
        <SecondaryDisplayWrapper>
          <LoadingSpinner size="lg" />
          <span>Confirming</span>
        </SecondaryDisplayWrapper>
      )

    case TransactionStatus.Approved:
      return (
        <SecondaryDisplayWrapper>
          <CircleCheck className="size-4 text-secondary" />
          <span>Approved</span>
        </SecondaryDisplayWrapper>
      )
    default:
      return (
        <SecondaryDisplayWrapper>
          <div className="relative grid aspect-square h-4 grid-cols-2 overflow-hidden rounded-full p-[3px] ring-[1.5px] ring-inset ring-gray-600 dark:ring-gray-400">
            <div className="h-full w-full rounded-l-full bg-gray-600 dark:bg-gray-400" />
          </div>
          <span>
            {approvalsLeft ?? 0} approval{approvalsLeft === 1 ? '' : 's'} needed
          </span>
        </SecondaryDisplayWrapper>
      )
  }
}
